import { columnType, dataType } from 'components/table/types';
import { createTableColumns } from './tables.types';
import _, { keyBy } from 'lodash';
import { getCompanyNameByLanguage } from 'utils/index';
import { DashboardCategories, DashboardCategoriesPropertiesNames } from 'constants/balance.constants';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { Stack } from 'immutable';
import { Box, Typography, useTheme } from '@mui/material';
import { symbol } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';

export const tableData = (
	type: DashboardCategories,
	data: Record<string, any>[],
	t: any,
	companies: Record<string, any>[] = [],
	currencies: Record<string, any>[] = []
) => {
	const companiesObj = _.keyBy(companies, 'id');
	const getCompanyName = (companyId?: number) => {
		const company = _.get(companiesObj, String(companyId));
		return getCompanyNameByLanguage(company) || '';
	};

	switch (type) {
		case DashboardCategories.CUSTOMER_BALANCES_DETAIL_BY_CUSTOMERS:
			const dataByCompany: any[] = [];
			data.forEach((company) => {
				const ba = company?.balances || [];
				let cr = _.keyBy(ba, 'currencyCode');
				cr = _.mapValues(cr, (c: any) => c.amount);
				dataByCompany.push({
					customerName: getCompanyName(Number(company?.companyId)),
					hyper: company?.hyperAccountNumber,
					ccId: company?.ccId,
					...cr,
				});
			});
			return dataByCompany;

		default:
			return data.map((item) => ({
				...item,
				customerName: getCompanyName(Number(item?.companyId)),
				//createdAt: _.get(item, DashboardCategoriesPropertiesNames.CONVERSION_DATE),
				totalInShekel: _.get(item, DashboardCategoriesPropertiesNames.SUM_NIS),
				pendingSum: _.get(item, DashboardCategoriesPropertiesNames.SUM),
				symbol:
					item?.symbol ||
					_.get(item, DashboardCategoriesPropertiesNames.CURRENCY_SYMBOL) ||
					_.get(item, DashboardCategoriesPropertiesNames.FEE_SYMBOL),
				currencyName: _.get(item, DashboardCategoriesPropertiesNames.CURRENCY_NAME),
				totalSum: _.get(item, DashboardCategoriesPropertiesNames.SUM),
				accountsAmount: _.get(item, DashboardCategoriesPropertiesNames.NUMBER_OF_COMPANIES),
				feeInShekel: _.get(item, DashboardCategoriesPropertiesNames.FEE_IN_SHEKEL)
					? `${currencies?.find((currency: any) => currency?.code === 'ILS')?.symbol}${_.get(
							item,
							DashboardCategoriesPropertiesNames.FEE_IN_SHEKEL
					  )}`
					: '',
				plan: _.get(item, 'planName'),
				totalAccounts: _.get(item, 'accountsNumber'),
				planAmount: _.get(item, 'plansRevenue'),
				totalRevenue: _.get(item, 'totalRevenue'),
			}));
	}
};

export const buildColumns = (
	type: DashboardCategories,
	t: any,
	currencies: any,
	countries: any,
	data: any
): columnType[] => {
	const columns: columnType[] = [];
	const theme = useTheme();
	const tableColumns = createTableColumns(currencies, data);
	const currenciesCodes = _.keyBy(currencies, 'code');
	const navigate = useNavigate();

	if (_.has(tableColumns, type)) {
		_.keys(tableColumns[type]).map((element) => {
			let customValueHandle = {};
			if (['totalSum', 'pendingSum', 'paymentAmount', 'feeAmount'].includes(element)) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<>
								{value > 0
									? `${_.get(row, 'symbol')}${formatNumber(value)}`
									: `${_.get(row, 'symbol')}${formatNumber(value)}`}
							</>
						);
					},
				};
			}
			if (['ccSellAmount', 'ccBuyAmount'].includes(element)) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						const key = element === 'ccBuyAmount' ? 'clientBuyCurrency' : 'clientSellCurrency';
						const code = _.get(row, key);
						return (
							<>
								{_.get(currenciesCodes, code)?.symbol}
								{formatNumber(value)}
							</>
						);
					},
				};
			}
			if (['countryId', 'bankAccountCountryId'].includes(element)) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						//const code = _.get(row, key)
						return <>{_.find(countries, { id: value })?.name}</>;
					},
				};
			}
			if (element === 'currencyName') {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<Box textAlign={'center'} width={120}>
								<Typography
									color={theme.palette.primary.main}
									variant="pageTitle"
									sx={{ fontWeight: 400 }}
								>
									{_.get(row, 'symbol')}
								</Typography>
								<Box color={theme.palette.root.grey}>{value}</Box>
							</Box>
						);
					},
				};
			}
			if (['totalInShekel', 'totalRevenue', 'planAmount', 'totalFees'].includes(element)) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<Box sx={{ direction: 'rtl' }}>
								{value && `${value < 0 ? '-' : ''}₪${formatNumber(Math.abs(value))}`}
							</Box>
						);
					},
				};
			}
			if (
				['totalPaymentFeesAmountNis', 'totalConversionFeesAmountNis', 'totalPlanFeesNis', 'totalFees'].includes(
					element
				)
			) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<Box sx={{ direction: 'rtl' }}>
								{value && `${value < 0 ? '-' : ''}₪${formatNumber(Math.abs(value))}`}
							</Box>
						);
					},
				};
			}
			if (element === 'dealType') {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return <Box>{value === 'sell' ? 'מכירה' : 'קנייה'}</Box>;
					},
				};
			}
			if (_.has(currenciesCodes, element)) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return <Box>{value && _.get(currenciesCodes, element)?.symbol + '' + formatNumber(value)}</Box>;
					},
				};
			}
			const props: columnType = {
				fieldId: 'id',
				name: element,
				title: t('page.dashboard.dashboardBalances.table.headers.' + element, element),
				type: tableColumns[type][element],
				sortable: true,
				...customValueHandle,
			};
			columns.push(props);
		});
	}
	return columns;
};

export const getRowAction = (type: DashboardCategories, navigate: any) => {
	return [DashboardCategories.MONTHLY_SUBSCRIPTIONS].includes(type)
		? {
				rowAction: (row: dataType) => {
					navigate(
						`/${pagesEnum.MAIN_DASHBOARD}/${DashboardCategories.MONTHLY_SUBSCRIPTIONS_BY_CUSTOMERS}/${row.planName}`
					);
				},
		  }
		: {};
};
